/**
 * Plan of Record — nested layout inside the prioritization shell.
 *
 * Renders PoR subsection navigation beside page content (same pattern as Settings
 * sub-pages): no second full-window chrome — the primary app sidebar stays visible.
 */

import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, GaugeCircle, History, CheckSquare2, LayoutTemplate } from 'lucide-react';
import {
  POR_SECTION_LABELS,
  type PorSection,
  type PorSectionStatus,
} from '../../types/plan-of-record';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';
import { useMobileExperience } from '../../mobile/featureFlag';
import { useActivePors } from '../../hooks/useActivePors';
import { usePorPageVisibility } from '../../hooks/usePorPageVisibility';

const SECTION_DOT: Record<PorSectionStatus, string> = {
  draft: 'bg-transparent',
  in_review: 'bg-primary-400',
  approved: 'bg-primary-600',
};

/** Jump among approved Plans of Record. Renders nothing unless several are active. */
function ActivePorSwitcher({
  porId,
  className,
  defaultSegment,
}: {
  porId: string;
  className?: string;
  defaultSegment: string;
}) {
  const navigate = useNavigate();
  const { porHref } = usePlanInstrumentNav();
  const activePors = useActivePors();

  if (activePors.length < 2) return null;
  const onActivePor = activePors.some((p) => p.id === porId);

  return (
    <div className={className}>
      <label className="sr-only" htmlFor="active-por-switcher">
        Switch Plan of Record
      </label>
      <select
        id="active-por-switcher"
        value={onActivePor ? porId : ''}
        onChange={(e) => {
          if (e.target.value && e.target.value !== porId) {
            navigate(porHref(e.target.value, defaultSegment));
          }
        }}
        className="w-full text-xs border border-[var(--rule)] px-2 py-1.5 text-[var(--text-sec)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:border-[var(--rule)] dark:bg-transparent dark:text-[var(--text-sec)]"
      >
        {!onActivePor && <option value="">Active Plans of Record…</option>}
        {activePors.map((p) => (
          <option key={p.id} value={p.id}>
            {p.is_primary ? '★ ' : ''}{p.title} · v{p.version}
          </option>
        ))}
      </select>
    </div>
  );
}

function navClass(isActive: boolean): string {
  return [
    'flex items-center gap-2.5 pl-3 pr-2 py-2 text-sm  border-l-2 transition-colors',
    isActive
      ? 'border-primary-600 bg-primary-50 text-primary-900 font-medium dark:border-primary-500 dark:bg-primary-950/30 dark:text-[var(--text-pri)]'
      : 'border-transparent text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]',
  ].join(' ');
}

function MobilePorNav({
  porId,
  title,
  sections,
}: {
  porId: string;
  title: string;
  sections: PorSection[];
}) {
  const navigate = useNavigate();
  const { instrumentCatalogHref, porHref } = usePlanInstrumentNav();
  const sectionByType = new Map(sections.map((s) => [s.section_type, s]));
  const { isVisible, visibleSectionOrder, defaultLandingSegment } = usePorPageVisibility(porId);

  const mobileTabClass = (isActive: boolean) =>
    [
      'flex-shrink-0 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
      isActive
        ? 'border-primary-600 text-primary-700'
        : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)]',
    ].join(' ');

  return (
    <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--rule)]">
        <button
          type="button"
          onClick={() => navigate(instrumentCatalogHref)}
          className="text-[var(--text-ter)] p-1 -ml-1 hover:bg-[var(--hover-tint)]"
          aria-label="Back to all Plans of Record"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">Plan of Record</div>
          <div className="text-sm font-semibold text-[var(--text-pri)] truncate">{title}</div>
        </div>
      </div>
      <ActivePorSwitcher porId={porId} className="border-b border-[var(--rule)] px-4 py-2" defaultSegment={defaultLandingSegment} />
      <nav className="flex overflow-x-auto scrollbar-none">
        {isVisible('dashboard') && (
          <NavLink to={porHref(porId, 'dashboard')} className={({ isActive }) => mobileTabClass(isActive)}>
            Dashboard
          </NavLink>
        )}
        {isVisible('priorities') && (
          <NavLink to={porHref(porId, 'priorities')} className={({ isActive }) => mobileTabClass(isActive)}>
            Priorities
          </NavLink>
        )}
        {visibleSectionOrder.map((type, idx) => {
          const section = sectionByType.get(type);
          return (
            <NavLink
              key={type}
              to={porHref(porId, `section/${type}`)}
              className={({ isActive }) => mobileTabClass(isActive)}
            >
              <span className="font-mono text-[10px] text-[var(--text-ter)] mr-1">{String(idx + 1).padStart(2, '0')}</span>
              {POR_SECTION_LABELS[type]}
              {section && section.status !== 'draft' && (
                <span className={`ml-1.5 h-1.5 w-1.5 rounded-full inline-block ${SECTION_DOT[section.status]}`} />
              )}
            </NavLink>
          );
        })}
        {isVisible('change_log') && (
          <NavLink to={porHref(porId, 'change-log')} className={({ isActive }) => mobileTabClass(isActive)}>
            Change Log
          </NavLink>
        )}
        <NavLink to={porHref(porId, 'layout')} className={({ isActive }) => mobileTabClass(isActive)}>
          Layout
        </NavLink>
      </nav>
    </div>
  );
}

export function PorSidebar({
  porId,
  title,
  sections,
}: {
  porId: string;
  title: string;
  sections: PorSection[];
}) {
  const isMobile = useMobileExperience();
  const navigate = useNavigate();
  const { instrumentCatalogHref, porHref } = usePlanInstrumentNav();
  const sectionByType = new Map(sections.map((s) => [s.section_type, s]));
  const { isVisible, visibleSectionOrder, defaultLandingSegment } = usePorPageVisibility(porId);

  if (isMobile) {
    return <MobilePorNav porId={porId} title={title} sections={sections} />;
  }

  return (
    <aside
      className="sticky top-1 z-10 w-64 shrink-0 flex flex-col max-h-[calc(100vh-6.5rem)] overflow-hidden border border-[var(--rule)] bg-[var(--bg-raised)] shadow-sm dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:shadow-none"
      aria-label="Plan of Record navigation"
    >
      <div className="flex h-14 items-center gap-2 border-b border-[var(--rule)] px-4 dark:border-[var(--rule)] shrink-0">
        <button
          type="button"
          onClick={() => navigate(instrumentCatalogHref)}
          className="text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] p-1 -ml-1 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
          aria-label="Back to all Plans of Record"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Plan of Record
          </div>
          <div className="text-sm font-semibold text-[var(--text-pri)] truncate dark:text-[var(--text-pri)]">{title}</div>
        </div>
      </div>

      <ActivePorSwitcher
        porId={porId}
        className="border-b border-[var(--rule)] px-3 py-2 dark:border-[var(--rule)] shrink-0"
        defaultSegment={defaultLandingSegment}
      />

      <nav className="flex-1 overflow-y-auto py-2 pr-2 pl-2 space-y-0.5 min-h-0">
        {isVisible('dashboard') && (
          <NavLink to={porHref(porId, 'dashboard')} className={({ isActive }) => navClass(isActive)}>
            <GaugeCircle className="h-4 w-4 flex-shrink-0" />
            <span>Executive Dashboard</span>
          </NavLink>
        )}
        {isVisible('priorities') && (
          <NavLink to={porHref(porId, 'priorities')} className={({ isActive }) => navClass(isActive)}>
            <CheckSquare2 className="h-4 w-4 flex-shrink-0" />
            <span>Strategic Priorities</span>
          </NavLink>
        )}

        {visibleSectionOrder.length > 0 && (
          <div className="pt-3 pb-1 pl-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            The record
          </div>
        )}

        {visibleSectionOrder.map((type, idx) => {
          const section = sectionByType.get(type);
          const num = String(idx + 1).padStart(2, '0');
          return (
            <NavLink
              key={type}
              to={porHref(porId, `section/${type}`)}
              className={({ isActive }) => navClass(isActive)}
            >
              <span className="text-[11px] font-mono text-[var(--text-ter)] w-5 flex-shrink-0">{num}</span>
              <span className="truncate flex-1">{POR_SECTION_LABELS[type]}</span>
              {section && (
                <span
                  className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${SECTION_DOT[section.status]}`}
                  title={section.status}
                />
              )}
            </NavLink>
          );
        })}

        {isVisible('change_log') && (
          <>
            <div className="pt-3 pb-1 pl-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Audit
            </div>
            <NavLink to={porHref(porId, 'change-log')} className={({ isActive }) => navClass(isActive)}>
              <History className="h-4 w-4 flex-shrink-0" />
              <span>Change Log</span>
            </NavLink>
          </>
        )}

        <div className="pt-3 pb-1 pl-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Configure
        </div>
        <NavLink to={porHref(porId, 'layout')} className={({ isActive }) => navClass(isActive)}>
          <LayoutTemplate className="h-4 w-4 flex-shrink-0" />
          <span>Page layout</span>
        </NavLink>
      </nav>
    </aside>
  );
}

export function PorLayout({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  const isMobile = useMobileExperience();

  if (isMobile) {
    return (
      <div className="flex flex-col min-h-0">
        {/* On mobile the sidebar renders as a horizontal scroll strip at the top */}
        {sidebar}
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    );
  }

  return (
    <div className="flex gap-8 items-start">
      {sidebar}
      <div className="flex-1 min-w-0 max-w-[1400px]">{children}</div>
    </div>
  );
}
